import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  AUTOSAVE_DELAY_MS,
  AUTOSAVE_START,
  type AutosaveEvent,
  autosaveAfter,
  type AutosaveState,
  whenDue,
} from './autosave';

export interface AutosaveOptions {
  /** Whether the step keeps drafts now: not once it is committed. */
  readonly enabled: boolean;
  /** Whether the quote is being written otherwise: a commit, or a save the broker asked for. */
  readonly writing: boolean;
  /** Whether the step holds a change the server doesn't have: against what it last kept, not what the step opened with. */
  readonly isDirty: () => boolean;
  /**
   * Keeps the step's draft as it stands, without a word to the broker. True once kept; false when refused for a reason
   * the broker has been told, as a conflict is. Rejects when it failed.
   */
  readonly save: () => Promise<boolean>;
  readonly delay?: number;
}

export interface Autosaver {
  /** The broker changed something: a save follows once they stop, or once a save under way has finished. */
  readonly changed: () => void;
  /**
   * Drops a save still waiting, and waits for one under way: for a save the broker asked for, which keeps everything
   * and must not race one of these.
   */
  readonly settle: () => Promise<void>;
  /**
   * Whether a save is waiting, under way, or queued behind one: until it has answered, what the step holds is not
   * known to be kept, though it may read the same as what was kept before it, as a change undone during a save does.
   */
  readonly unsettled: () => boolean;
  readonly start: () => void;
  readonly stop: () => void;
}

/**
 * The timing behind a step's autosave, out of React: `read` gives the step's options as they are now, and `tell` is
 * told what happened, for the step's footer to say.
 */
export function createAutosaver(
  read: () => AutosaveOptions,
  tell: (event: AutosaveEvent) => void
): Autosaver {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let saving: Promise<void> | null = null;
  let queued = false;
  let active = true;

  const clear = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
  const arm = () => {
    clear();
    timer = setTimeout(due, read().delay ?? AUTOSAVE_DELAY_MS);
  };

  const run = () => {
    tell('started');
    saving = read()
      .save()
      .then(
        (kept) => tell(kept ? 'kept' : 'refused'),
        () => tell('failed')
      )
      .then(() => {
        saving = null;
        // Changes made while it saved are saved together, once the broker stops again.
        if (queued && active) {
          queued = false;
          arm();
        }
      });
  };

  function due() {
    timer = undefined;
    if (!active) {
      return;
    }
    const { enabled, writing, isDirty } = read();
    const busy = saving !== null || writing;
    const decision = whenDue({
      enabled,
      saving: saving !== null,
      writing,
      dirty: enabled && !busy && isDirty(),
    });
    if (decision === 'later') {
      arm();
    } else if (decision === 'skip') {
      tell('unchanged');
    } else {
      run();
    }
  }

  return {
    changed: () => {
      if (!active || !read().enabled) {
        return;
      }
      if (saving !== null) {
        queued = true;
        tell('changed');
        return;
      }
      if (!read().isDirty()) {
        clear();
        tell('unchanged');
        return;
      }
      tell('changed');
      arm();
    },
    settle: async () => {
      clear();
      queued = false;
      if (saving !== null) {
        await saving;
      }
    },
    unsettled: () => saving !== null || queued || timer !== undefined,
    start: () => {
      active = true;
    },
    stop: () => {
      active = false;
      clear();
    },
  };
}

/**
 * How long autosave waits for the broker to stop; null turns it off. The app leaves it as it is. The library's tests
 * turn it off unless they are about autosave, so a slow machine's pause between a test's edits isn't taken for the
 * broker stopping, and a test counting what was written counts what it did.
 */
export const AutosaveDelay = createContext<number | null>(AUTOSAVE_DELAY_MS);

/**
 * Keeps a step's draft a short while after the broker stops changing it. The step tells it of each change, and says
 * in its footer what `state` says. Nothing is saved while the step is committed, while the quote is being written
 * otherwise, or for a change the step made itself, which `isDirty` doesn't count.
 */
export function useAutosave(
  options: AutosaveOptions
): Readonly<{ state: AutosaveState; autosaver: Autosaver }> {
  const delay = useContext(AutosaveDelay);
  const withDelay = {
    ...options,
    enabled: options.enabled && delay !== null,
    delay: options.delay ?? delay ?? undefined,
  };
  const latest = useRef(withDelay);
  latest.current = withDelay;
  const [state, dispatch] = useReducer(autosaveAfter, AUTOSAVE_START);
  const [autosaver] = useState(() =>
    createAutosaver(() => latest.current, dispatch)
  );
  useEffect(() => {
    autosaver.start();
    return () => autosaver.stop();
  }, [autosaver]);
  return { state, autosaver };
}
